import {
    Box,
    Button,
    Card,
    FormControl,
    FormLabel,
    Input,
    Stack,
    Textarea,
    Typography,
} from "@mui/joy";
import React, { useState } from "react";
import { Mail, MessageSquare } from "lucide-react";
import { motion } from "motion/react";
import { gradientPairs } from "../../theme";
import GuestNavbar from "../../components/Layout/GuestNavbar";
import {useTranslation} from "react-i18next";

const Contact = () => {
    const [formData, setFormData] = useState({ name: "", email: "", subject: "", message: "" });
    const {t}=useTranslation()

    const handleChange = (field: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setFormData((prev) => ({ ...prev, [field]: e.target.value }));
    }

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault()
        console.log("Contact form:", formData)
    }

    return (
        <Stack sx={{ minHeight: "100vh", overflowX: "hidden" }}>
            <GuestNavbar active="contact" />

            <Stack
                paddingX={{ xs: 2, sm: 4, lg: 6 }}
                paddingY={{ xs: 4, md: 6 }}
                gap={4}
                alignItems="center"
            >
                <motion.div
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4 }}
                    style={{ textAlign: "center", maxWidth: 560 }}
                >
                    <Typography
                        level="h1"
                        sx={{ fontSize: { xs: "1.75rem", md: "2.25rem" }, marginBottom: 1 }}
                    >
                        {t("contactPageTitle")}
                    </Typography>
                    <Typography level="body-lg" textColor="neutral.600">
                        {t("contactPageDescription")}
                    </Typography>
                </motion.div>

                <Stack
                    direction={{ xs: "column", md: "row" }}
                    gap={3}
                    sx={{ width: "100%", maxWidth: 900 }}
                >
                    <motion.div
                        initial={{ opacity: 0, x: -16 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.4, delay: 0.1 }}
                        style={{ flex: 1 }}
                    >
                        <Card
                            variant="outlined"
                            sx={{
                                padding: 3,
                                borderRadius: 2,
                            }}
                            data-no-glow
                            data-no-animation
                        >
                            <Typography level="title-lg" sx={{ marginBottom: 2 }}>
                                {t("contactFormTitle")}
                            </Typography>
                            <Stack gap={2}>
                                <Stack direction="row" gap={1.5} alignItems="center">
                                    <Mail size={20} style={{ color: "var(--joy-palette-primary-500)" }} />
                                    <Box>
                                        <Typography level="body-sm" textColor="neutral.500">
                                            {t("contactInfoEmailLabel")}
                                        </Typography>
                                        <Typography level="body-md">support@linguascore.com</Typography>
                                    </Box>
                                </Stack>
                                <Stack direction="row" gap={1.5} alignItems="center">
                                    <MessageSquare size={20} style={{ color: "var(--joy-palette-primary-500)" }} />
                                    <Box>
                                        <Typography level="body-sm" textColor="neutral.500">
                                            {t("contactInfoResponseTimeLabel")}
                                        </Typography>
                                        <Typography level="body-md">
                                            {t("contactInfoResponseTimeValue")}
                                        </Typography>
                                    </Box>
                                </Stack>
                            </Stack>
                        </Card>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, x: 16 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.4, delay: 0.15 }}
                        style={{ flex: 1.2 }}
                    >
                        <Card
                            variant="outlined"
                            sx={{
                                padding: 3,
                                borderRadius: 2,
                            }}
                            data-no-glow
                            data-no-animation
                        >
                            <Typography level="title-lg" sx={{ marginBottom: 2 }}>
                                {t("contactFormTitle")}
                            </Typography>
                            <Box component="form" onSubmit={handleSubmit}>
                                <Stack gap={2}>
                                    <FormControl>
                                        <FormLabel>
                                            {t("contactFormNameLabel")}
                                        </FormLabel>
                                        <Input
                                            placeholder={t("contactFormNamePlaceholder")}
                                            value={formData.name}
                                            onChange={handleChange("name")}
                                            required
                                        />
                                    </FormControl>
                                    <FormControl>
                                        <FormLabel>{t("contactFormEmailLabel")}</FormLabel>
                                        <Input
                                            type="email"
                                            placeholder={t("contactFormEmailPlaceholder")}
                                            value={formData.email}
                                            onChange={handleChange("email")}
                                            required
                                        />
                                    </FormControl>
                                    <FormControl>
                                        <FormLabel>{t("contactFormSubjectLabel")}</FormLabel>
                                        <Input
                                            placeholder={t("contactFormSubjectPlaceholder")}
                                            value={formData.subject}
                                            onChange={handleChange("subject")}
                                        />
                                    </FormControl>
                                    <FormControl>
                                        <FormLabel>{t("contactFormMessageLabel")}</FormLabel>
                                        <Textarea
                                            placeholder={t("contactFormMessagePlaceholder")}
                                            minRows={4}
                                            value={formData.message}
                                            onChange={handleChange("message")}
                                            required
                                        />
                                    </FormControl>
                                    <Button
                                        type="submit"
                                        size="lg"
                                        sx={{
                                            marginTop: 1,
                                        }}>
                                        {t("contactFormSubmitActionLabel")}
                                    </Button>
                                </Stack>
                            </Box>
                        </Card>
                    </motion.div>
                </Stack>
            </Stack>
        </Stack>
    );
};

export default Contact;
